// QuizContext.js
import { createContext, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { usePesaParcours } from '../stores/usePesaParcours';

const QuizContext = createContext();

export const useQuiz = () => {
  return useContext(QuizContext);
};

export const QuizProvider = ({ children }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { userId, isAuthenticated, loading: authLoading } = useAuth();
  const { currentParcours, updateProgressionModule } = usePesaParcours();
  const [quizResult, setQuizResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const parcoursId = currentParcours?._id;

  const fetchQuiz = useCallback(async (moduleId) => {
    if (!moduleId || typeof moduleId !== 'string' || !/^[0-9a-fA-F]{24}$/.test(moduleId)) {
      console.error('Invalid moduleId:', moduleId);
      setError('ID de module invalide pour récupérer le quiz.');
      toast.error('ID de module invalide.');
      setIsLoading(false);
      return;
    }

    console.log(`Fetching quiz for moduleId: ${moduleId}`);
    setIsLoading(true);
    setError(null);
    try {
      console.log('API CALL: GET /modules/', moduleId, '/quiz');
      const response = await api.get(`/modules/${moduleId}/quiz`);
      console.log('API RESPONSE:', response.status, response.data);
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers['retry-after'] || '5', 10) * 1000;
        console.log(`Too many requests. Retrying after ${retryAfter / 1000} seconds.`);
        await new Promise(resolve => setTimeout(resolve, retryAfter));
        return fetchQuiz(moduleId);
      }

      if (response.status === 200) {
        const data = response.data;
        if (data && data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
          setQuiz(data);
          setAnswers({});
          setScore(0);
          setIsSubmitted(false);
          console.log('Quiz data set successfully', data.questions.length);
        } else {
          console.error('Invalid or empty quiz data:', data);
          setError('Invalid or no questions in quiz');
          toast.error('Quiz invalide ou sans questions.');
        }
      } else {
        console.error('Error loading quiz:', response.statusText);
        setError('Error loading quiz');
        toast.error('Erreur lors du chargement du quiz.');
      }
    } catch (error) {
      console.error('Network error:', error.message);
      setError('Network error: ' + (error.response?.data?.message || error.message));
      toast.error('Erreur réseau : ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
      console.log('Quiz loading completed:', { isLoading, quiz, error });
    }
  }, []);

  const updateAnswer = (questionId, value) => {
    console.log(`Updating answer for questionId ${questionId} to value:`, value);
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const submitQuiz = useCallback(async (parcoursId, moduleId, answers) => {
  console.log('submitQuiz appelé avec:', { parcoursId, moduleId, userId });

  // === VALIDATIONS ===
  if (!moduleId || !/^[0-9a-fA-F]{24}$/.test(moduleId)) {
    setError('ID de module invalide.');
    toast.error('ID de module invalide.');
    return;
  }

  if (authLoading || !isAuthenticated || !quiz || !parcoursId || !userId) {
    const msg = !isAuthenticated ? 'Connectez-vous pour soumettre.' : 'Données manquantes.';
    setError(msg);
    toast.error(msg);
    return;
  }

  if (!/^[0-9a-fA-F]{24}$/.test(parcoursId) || !/^[0-9a-fA-F]{24}$/.test(userId)) {
    setError('Format d\'ID invalide');
    toast.error('Format d\'ID invalide.');
    return;
  }

  // === CALCUL DU SCORE (POURCENTAGE + OPEN VALIDÉES) ===
  const maxPoints = quiz.questions.reduce((sum, q) => sum + (q.points || 1), 0);
  let earnedPoints = 0;
  const processedAnswers = [];

  quiz.questions.forEach((question) => {
    const userAnswer = answers[question._id.toString()];
    let isCorrect = false;
    let pointsEarned = 0;

    if (userAnswer !== undefined && userAnswer !== null && String(userAnswer).trim() !== '') {
      if (question.type === 'open') {
        // === OPEN = VALIDÉ SI REMPLI ===
        isCorrect = true;
        pointsEarned = question.points || 1;
      } else {
        // === QUESTIONS FERMÉES ===
        if (Array.isArray(question.correctAnswer)) {
          const userArr = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
          isCorrect = question.correctAnswer.every(ca => userArr.includes(ca));
        } else {
          const normalizedUser = typeof userAnswer === 'string' ? userAnswer.trim().toLowerCase() : String(userAnswer);
          const normalizedCorrect = typeof question.correctAnswer === 'string' ? question.correctAnswer.trim().toLowerCase() : String(question.correctAnswer);
          isCorrect = normalizedUser === normalizedCorrect;
        }
        if (isCorrect) pointsEarned = question.points || 1;
      }
    }

    if (isCorrect) earnedPoints += pointsEarned;

    processedAnswers.push({

      questionId: question._id.toString(),
      selectedAnswer: userAnswer,
      isCorrect,
      pointsEarned,
    });
  });

  const percentageScore = maxPoints > 0 ? Math.round((earnedPoints / maxPoints) * 100) : 0;
  const passedLocal = percentageScore >= 70;

  console.log('Score calculé:', percentageScore + '%', 'Points:', earnedPoints + '/' + maxPoints);

  // === MISE À JOUR UI IMMÉDIATE (fallback si backend crash) ===
  setScore(percentageScore);
  setIsSubmitted(true);
  setQuizResult({
    score: percentageScore,
    totalPoints: maxPoints,
    passed: passedLocal,
    attempts: []
  });
  setShowResult(true);

  toast.success(`Quiz terminé ! Score: ${percentageScore}% ${passedLocal ? '🎉' : '💪'}`);

  // === ENVOI BACKEND ===
  try {
    const response = await updateProgressionModule(parcoursId, moduleId, {
      quizId: quiz._id,
      score: percentageScore,  // ← POURCENTAGE, PAS POINTS BRUTS
      answers: processedAnswers,
      userId,
      type: 'quiz'
    });

    console.log('Réponse backend:', response);

    if (response?.payload?.quizResult) {
      setQuizResult(prev => ({
        ...prev,
        passed: response.payload.quizResult.passed,
        attempts: response.payload.quizResult.attempts || []
      }));
    }

    // Redirection seulement si passé
    //if (response?.payload?.quizResult?.passed ?? passedLocal) {
      //setTimeout(() => setActiveTab('overview'), 8000);
    //}

  } catch (error) {
    console.error('Backend en erreur, mais UI OK:', error);
    toast.warn('Sauvegarde échouée, mais score local conservé.');
    // UI déjà à jour → pas de blocage
    //if (passedLocal) {
      //setTimeout(() => setActiveTab('overview'), 8000);
    //}
  }
}, [
  quiz,
  userId,
  updateProgressionModule,
  authLoading,
  isAuthenticated,
  //setActiveTab // ← AJOUTE ÇA DANS LES DEPENDANCES
]);

  const value = {
    quiz,
    answers,
    score,
    isSubmitted,
    fetchQuiz,
    updateAnswer,
    submitQuiz,
    isLoading,
    error,
    // AJOUTÉS ICI
    quizResult,
    showResult,
    setQuizResult,     // si tu veux reset
    setShowResult      // si tu veux reset
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};